import {
  AppConfigService as ConfigService,
  DictionaryService,
  FocusService,
  FeatureFlagService,
  TokenService,
  GoogleTagManagerService
} from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { LenderService } from '@shared/service/lender/lender.service';
/* NOTE: Resolving 'eslint-disable prefer-arrow-functions'
 * will require architecture and refactoring of the entire WW engine
 */
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { OlaOnChangeFunctionsService } from '@workflow/ecp-workflow/ola/services/logic-functions/on-change-functions.service';
import { ApplicationApiService } from '../../service/application-api/application-api.service';
import { InstaTouchService } from '../../service/instaTouch/insta-touch.service';
import { L360Service } from '../../service/l360/l360.service';
import { OnChangeFunctionsService } from '../../service/logic-functions/on-change-functions.service';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { StatefulService } from '@app/service/stateful.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { GlobalService } from '@workflow/ecp-shared/service/global/global.service';
import { SubscriptionEmitService } from '@workflow/ecp-shared/service/subscription-emit/subscription-emit.service';
import { PATH_PAGE_ITERATION_SYMBOL } from '@workflow/class/stateful-value.class';
import { CoreControlType } from '@workflow/class/workflow.type';
// eslint-disable-next-line max-len
import { CoreBuildingBlockControlBaseComponent } from '@workflow/dynamic-render/building-block/shared/building-block-control-base.component';
import { BuildingBlockBase } from '@workflow/dynamic-render/building-block/shared/building-block.decorator';
import { CommonService } from '@workflow/legacy';
import { LogicService } from '@workflow/service/logic/logic.service';
import { StorageService } from '@workflow/service/storage/storage.service';
import { StringVariableService } from '@workflow/service/string-variable/string-variable.service';
import { WorkflowPageNavigationService } from '@workflow/service/workflow-page-navigation/workflow-page-navigation.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { AddressService } from '@shared/service/address.service';
import { HostPrefillService } from '../../service/host-prefill.service';
import { EcpWorkflowEnvironmentService } from '@workflow/ecp-shared/service/environment/environment.service';
import { DomSanitizer } from '@angular/platform-browser';
import { UserSessionService } from '@shared/service/user-session.service';
// eslint-disable-next-line max-len
import { WorkflowPageNavigationStateService } from '@workflow/service/workflow-page-navigation-state/workflow-page-navigation-state.service';
import { XApiKeyService } from '@shared/service/x-api-key.service';
import { UtilitiesService } from '@shared/service/utilities.service';
import { StateManagementService } from '@workflow/service/state-management/state-management.service';
import { WorkflowLoaderService } from '@shared/service/workflow-loader.service';
import { RuntimeConfigService } from '@app/service/runtime-config.service';

export type CustomControls =
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  | CoreControlType
  | 'accounts-and-services'
  | 'app-prefill'
  | 'asset-allocation'
  | 'disclosure'
  | 'dynamic-personal-info'
  | 'employments-retrieval'
  | 'generate-eligibility'
  | 'generate-custom-fields'
  | 'input-obfuscate'
  | 'info-retrieval-by-driver-license'
  | 'info-row-obfuscate'
  | 'joint-credit-select'
  | 'liabilities-retrieval'
  | 'oao-identity-verification'
  | 'ola-loan-type'
  | 'personal-info-retrieval'
  | 'textarea'
  | 'toggle'
  | 'generate-special-programs'
  | 'field-group-card'
  | 'econsent'
  | 'generate-requested-doc'
  | 'cross-sell'
  | 'payment-calc'
  | 'alloy-verification'
  | 'econsent-splash-screen'
  | 'smart-sign';

// eslint-disable-next-line prefer-arrow-functions/prefer-arrow-functions
export function BuildingBlock(controlType: CustomControls): (target) => void {
  return (target) => BuildingBlockBase<CustomControls>(controlType, target);
}

@Component({ template: '', standalone: false })
export class BuildingBlockControlBaseComponent extends CoreBuildingBlockControlBaseComponent {
  protected logger = inject(SawDust);

  constructor(
    protected readonly logicService: LogicService,
    changeDetectorRef: ChangeDetectorRef,
    addressService: AddressService,
    protected readonly googleTag: GoogleTagManagerService,
    public readonly applicationApiService: ApplicationApiService,
    public readonly dictionary: DictionaryService,
    public readonly router: Router,
    public readonly workflowService: WorkflowService,
    public readonly storageService: StorageService,
    public readonly runtimeConfigService: RuntimeConfigService,
    public readonly featureFlagService: FeatureFlagService,
    public readonly dynamicPageService: WorkflowPageNavigationService,
    protected readonly apiService: ApiService,
    protected readonly instaTouchService: InstaTouchService,
    public readonly lenderService: LenderService,
    public readonly stringVariableService: StringVariableService,
    public readonly l360Service: L360Service,
    public readonly olaOnChangeFunctionsService: OlaOnChangeFunctionsService,
    public readonly onChangeFunctionsService: OnChangeFunctionsService,
    public readonly formBuilder: UntypedFormBuilder,
    public readonly subscribeEmitService: SubscriptionEmitService,
    public readonly globalService: GlobalService,
    public readonly commonService: CommonService,
    public readonly activeRoute: ActivatedRoute,
    public focusService: FocusService,
    protected readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    public readonly userService: UserService,
    public readonly hostPrefillService: HostPrefillService,
    public readonly environmentService: EcpWorkflowEnvironmentService,
    public readonly sanitizer: DomSanitizer,
    public readonly tokenService: TokenService,
    public readonly statefulService: StatefulService,
    public readonly userSession: UserSessionService,
    public readonly pageNavigationStateService: WorkflowPageNavigationStateService,
    public readonly configService: ConfigService,
    public readonly xApiKeyService: XApiKeyService,
    public readonly utilitiesService: UtilitiesService,
    public readonly stateManager: StateManagementService,
    protected readonly loaderService: WorkflowLoaderService
  ) {
    super(logicService, changeDetectorRef, stringVariableService, addressService, featureFlagService);
  }

  /** Returns borrower id associated with field. Handles both primary and co applicant borrowerId */
  protected get borrowerId(): string {
    return this.statefulService.wrap(`api:${this.coAppSwitch}borrowerInfo/borrowerId`).valueString;
  }

  public set isAssetSubmitted(value: boolean) {
    this.statefulService.wrap(`api:${this.coAppSwitch}borrowerInfo/isAssetSubmitted`).value = value;
  }

  public get isAssetSubmitted(): boolean {
    return this.statefulService.wrap(`api:${this.coAppSwitch}borrowerInfo/isAssetSubmitted`).valueString === 'true';
  }

  /** if primary applicant */
  protected get isPrimary(): boolean {
    return !this.isCoApplicantField;
  }

  /** check if this is a field for co applicant */
  protected get isCoApplicantField(): boolean {
    return this.field.config.id.indexOf(PATH_PAGE_ITERATION_SYMBOL) > -1;
  }
  /** Root for storage base on co applicant status */
  protected get coAppSwitch(): string {
    return this.isCoApplicantField ? `coBorrowers/${this.field.pageIterationId}/` : '';
  }
}
